import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { Code, EmptyState, LoadingOverlay } from 'dashboard/components/common';
import RuleDialog from './RuleDialog';
import TaxPreviewDialog from './TaxPreviewDialog';

/**
 * Per-brand tax overrides + the platform default table. Most-specific
 * matching country/region/code wins; this tab also lets the owner run a
 * preview calculator.
 */
export default function TaxRulesTab({ brandId }) {
  const [rules, setRules] = useState([]);
  const [platformTable, setPlatformTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const reload = useCallback(() => {
    if (!brandId) return Promise.resolve();
    setLoading(true);
    return API.listTaxRules({ brand_id: brandId })
      .then((res) => setRules(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [brandId]);

  useEffect(() => {
    if (!brandId) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listTaxRules({ brand_id: brandId })
      .then((res) => { if (!cancelled) setRules(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load', { toastId: 'taxrules-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [brandId]);
  useEffect(() => {
    let cancelled = false;
    API.getPlatformTaxTable()
      .then((res) => { if (!cancelled) setPlatformTable(res); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const remove = (row) => {
    if (!window.confirm(`Remove ${row.name} for ${row.country}/${row.region || '*'}?`)) return;
    API.deleteTaxRule({ brand_id: brandId, _id: row._id })
      .then(() => { toast.success('Removed'); reload(); })
      .catch((err) => toast.error(err?.message || 'Failed to delete'));
  };

  return (
    <Box>
      <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap='10px' mb='14px'>
        <Box>
          <Typography fontWeight='700'>Tax rules</Typography>
          <Typography variant='body2' color='text.secondary'>
            Override the platform defaults for your jurisdictions. More-specific rules win.
          </Typography>
        </Box>
        <Stack direction='row' spacing='8px'>
          <Button variant='outlined' onClick={() => setPreviewOpen(true)}>Preview a sale</Button>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => { setEditing(null); setDlgOpen(true); }}
          >
            New rule
          </Button>
        </Stack>
      </Stack>

      <LoadingOverlay loading={loading && rules.length === 0} minHeight={180}>
      <TableContainer
        component={Box}
        sx={{
          border: '1px solid', borderColor: 'divider',
          borderRadius: '12px', overflowX: 'auto', mb: '20px',
        }}
      >
        <Table size='small'>
          <TableHead sx={{ bgcolor: 'action.hover' }}>
            <TableRow>
              <TableCell>Country / region</TableCell>
              <TableCell>Tax code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align='right'>Rate</TableCell>
              <TableCell>Flags</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rules.length === 0 && (
              <TableRow><TableCell colSpan={6}><EmptyState>No brand-specific rules yet. The platform defaults below are in effect.</EmptyState></TableCell></TableRow>
            )}
            {rules.map((r) => (
              <TableRow key={r._id}>
                <TableCell>
                  <Code>{r.country}</Code> / <Code>{r.region || '*'}</Code>
                </TableCell>
                <TableCell><Code>{r.tax_code || '*'}</Code></TableCell>
                <TableCell>
                  {r.name}{' '}
                  {!r.is_active && <Chip size='small' label='inactive' />}
                </TableCell>
                <TableCell align='right'>{(r.rate_basis_points / 100).toFixed(2)}%</TableCell>
                <TableCell>
                  <Stack direction='row' spacing='4px'>
                    {r.is_inclusive && <Chip size='small' color='secondary' label='incl.' />}
                    {r.is_compound && <Chip size='small' color='warning' label='compound' />}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction='row' spacing='4px'>
                    <Tooltip title='Edit'>
                      <IconButton size='small' onClick={() => { setEditing(r); setDlgOpen(true); }}>
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Delete'>
                      <IconButton size='small' color='error' onClick={() => remove(r)}>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </LoadingOverlay>

      <Typography fontWeight='700' variant='body2' mb='6px'>Platform defaults</Typography>
      <Typography variant='caption' color='text.secondary' mb='8px' display='block'>
        Used when no brand rule matches. Add a brand rule above to override.
      </Typography>
      <TableContainer
        component={Box}
        sx={{
          border: '1px solid', borderColor: 'divider',
          borderRadius: '12px', overflowX: 'auto',
        }}
      >
        <Table size='small'>
          <TableHead sx={{ bgcolor: 'action.hover' }}>
            <TableRow>
              <TableCell>Country</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align='right'>Rate</TableCell>
              <TableCell>Flags</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {platformTable.map((r, i) => (
              <TableRow key={`${r.country}|${r.region}|${r.name}|${i}`}>
                <TableCell><Code>{r.country}</Code></TableCell>
                <TableCell><Code>{r.region}</Code></TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell align='right'>{(r.rate_basis_points / 100).toFixed(2)}%</TableCell>
                <TableCell>
                  <Stack direction='row' spacing='4px'>
                    {r.is_inclusive && <Chip size='small' color='secondary' label='incl.' />}
                    {r.is_compound && <Chip size='small' color='warning' label='compound' />}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <RuleDialog
        open={dlgOpen}
        onClose={() => setDlgOpen(false)}
        brandId={brandId}
        editing={editing}
        onSaved={reload}
      />
      <TaxPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        brandId={brandId}
      />
    </Box>
  );
}
